import { useState } from 'react';
import {
  BugAntIcon,
  SparklesIcon,
  ArrowUpCircleIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Card, ContentLoader } from '@/components/common';
import { useDevLogs } from '@/hooks/useDevLog';
import type { DevLogEntry } from '@/services/devlog';

const categoryConfig: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  bugfix: { icon: BugAntIcon, color: 'text-red-600', bg: 'bg-red-50' },
  feature: { icon: SparklesIcon, color: 'text-blue-600', bg: 'bg-blue-50' },
  improvement: { icon: ArrowUpCircleIcon, color: 'text-green-600', bg: 'bg-green-50' },
  security: { icon: ShieldCheckIcon, color: 'text-orange-600', bg: 'bg-orange-50' },
  breaking: { icon: ExclamationTriangleIcon, color: 'text-purple-600', bg: 'bg-purple-50' },
};

export function DevLogPage() {
  const [page] = useState(1);
  const { data, isLoading } = useDevLogs(page);

  if (isLoading) return <ContentLoader />;

  // Group entries by version
  const grouped: Record<string, DevLogEntry[]> = {};
  for (const entry of data?.results || []) {
    if (!grouped[entry.version]) grouped[entry.version] = [];
    grouped[entry.version].push(entry);
  }

  return (
    <div>
      <PageHeader
        title="Dev Log"
        subtitle="Platform release notes, bug fixes, and new features"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Dev Log' }]}
      />

      {Object.keys(grouped).length === 0 && (
        <Card>
          <div className="text-center py-12 text-gray-500">
            No release notes yet.
          </div>
        </Card>
      )}

      <div className="space-y-8">
        {Object.entries(grouped).map(([version, entries]) => (
          <div key={version}>
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                v{version}
              </span>
              <span className="text-sm text-gray-500">
                {entries[0]?.release_date}
              </span>
            </div>

            <div className="space-y-3">
              {entries.map((entry) => {
                const config = categoryConfig[entry.category] || categoryConfig.improvement;
                const Icon = config.icon;

                return (
                  <Card key={entry.id}>
                    <div className="flex gap-4">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color}`}>
                            {entry.category_display}
                          </span>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {entry.title}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
                          {entry.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
